import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PRISM_PHONE, PRISM_PHONE_HREF } from "./prismData";

/**
 * Section A — "Do these feel familiar?"
 * Placed directly after the hero, before "If trauma is still shaping your days".
 * 4-card grid (2x2 desktop, stacked mobile) of the four PTSD symptom patterns.
 */
const cards = [
  {
    title: "When the past breaks into the present",
    label: "Intrusion",
    items: [
      "Memories or images that arrive uninvited",
      "Waves of emotion that seem to come from nowhere",
      "Nightmares that repeat a feeling or theme, not always the exact event",
      "The sense that your own mind can hijack you",
    ],
  },
  {
    title: "When your life is built around not feeling",
    label: "Avoidance",
    items: [
      "Steering clear of certain places, people or conversations",
      "Staying so busy there's no room to think",
      "Going numb instead of feeling, which is protection, not personality",
    ],
  },
  {
    title: "When you stop feeling like yourself",
    label: "Mood and thinking",
    items: [
      "Feeling detached from people you love",
      "Guilt or shame that won't let go",
      "Losing interest in things that used to matter",
      "\u201CI'm here, but I'm not really here\u201D",
    ],
  },
  {
    title: "When your body won't stand down",
    label: "Hyperarousal",
    items: [
      "Always scanning, always on guard",
      "Startling easily",
      "Trouble falling or staying asleep",
      "A shorter fuse than you'd like",
      "Feeling unsafe even when you know you're safe",
    ],
  },
];

export default function SymptomsSection() {
  return (
    <section
      className="relative bg-light-blue/40 py-16 lg:py-24"
      aria-labelledby="prism-symptoms-heading"
    >
      <div className="container-px mx-auto max-w-5xl">
        <div className="max-w-3xl">
          <span className="text-sm font-semibold uppercase tracking-widest text-accent">
            Understanding your symptoms
          </span>
          <h2
            id="prism-symptoms-heading"
            className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]"
          >
            It may not be &ldquo;just how you are.&rdquo; It may be PTSD.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Many people live with PTSD for years without a name for it. They
            just know something feels off, or that they haven&rsquo;t felt like
            themselves in a long time. PTSD tends to show up in four patterns.
            You don&rsquo;t need all of them, and you don&rsquo;t need a single
            dramatic event to explain them.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
          {cards.map((card) => (
            <div
              key={card.title}
              className="rounded-2xl border border-border bg-card p-6 shadow-soft transition-all duration-300 hover:border-accent/40 hover:shadow-premium sm:p-7"
            >
              <h3 className="font-headings text-lg font-semibold text-primary">
                {card.title}
              </h3>
              <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-medium-blue">
                ({card.label})
              </p>
              <ul className="mt-4 space-y-2.5">
                {card.items.map((item) => (
                  <li
                    key={item}
                    className="flex gap-2.5 text-[15px] leading-relaxed text-muted-foreground"
                  >
                    <span
                      aria-hidden="true"
                      className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="mt-10 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          These aren&rsquo;t character flaws. They&rsquo;re patterns the brain
          learns after trauma or long-term stress, and patterns can be
          retrained. That&rsquo;s what Prism is designed to help with.
        </p>

        <div className="mt-8">
          <Button
            asChild
            size="lg"
            className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
          >
            <a href={PRISM_PHONE_HREF}>
              <Phone className="mr-2 h-4 w-4" />
              Talk with our team
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
